import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

const STEPS = [
  ["Select your format", "Choose a box of 10 vials — 3 mL or 10 mL — and the number of boxes."],
  ["Add to cart or enquire", "Use the cart for a quick summary, or send us an enquiry for bulk and institutional orders."],
  ["Confirmation", "Please allow for confirmation within 3 business days. We confirm availability, pricing, and a shipping quote by email."],
  ["Dispatch", "Vials ship in protective packaging with tamper-evident seals. Continental US only — no AK/HI/PR or international shipping."],
];

export default function OrderSection() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received", {
        description: "Please allow for confirmation within 3 business days.",
      });
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error("Could not send enquiry", {
        description: "Please email orders@lab2o.com directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="order" className="py-24 sm:py-32 bg-white" data-testid="order-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-plexmono text-[11px] tracking-[0.25em] uppercase text-slate-400 mb-4"
          data-testid="order-overline"
        >
          04 — Ordering
        </motion.p>

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-chivo font-black tracking-tighter text-4xl sm:text-5xl text-slate-950 mb-10" data-testid="order-heading">
              How to order.
            </h2>
            <ol className="flex flex-col">
              {STEPS.map(([title, body], i) => (
                <li key={title} className="flex gap-6 py-6 border-t border-slate-200" data-testid={`order-step-${i + 1}`}>
                  <span className="font-chivo font-black text-3xl text-outline leading-none pt-1 shrink-0 w-14">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-chivo font-bold text-lg text-slate-950">{title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-8 border border-slate-200 bg-slate-50 px-5 py-4 flex items-center gap-3" data-testid="order-email-note">
              <Mail className="w-4 h-4 text-slate-500 shrink-0" />
              <p className="font-plexmono text-xs tracking-wide text-slate-600">
                orders@lab2o.com — we ship to laboratory and business addresses within the continental US
              </p>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="border border-slate-200 p-7 sm:p-10 self-start"
            data-testid="order-enquiry-form"
          >
            <h3 className="font-chivo font-extrabold tracking-tight text-2xl text-slate-950 mb-2">
              Send an enquiry
            </h3>
            <p className="text-sm text-slate-500 mb-8">
              Tell us what your laboratory needs. Research reagents only.
            </p>

            <div className="flex flex-col gap-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="enquiry-name" className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500">Name *</Label>
                  <Input id="enquiry-name" required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="enquiry-name-input" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="enquiry-email" className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500">Email *</Label>
                  <Input id="enquiry-email" type="email" required value={form.email} onChange={set("email")} className="rounded-sm" data-testid="enquiry-email-input" />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="enquiry-org" className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500">Organisation / Lab</Label>
                <Input id="enquiry-org" value={form.organization} onChange={set("organization")} className="rounded-sm" data-testid="enquiry-org-input" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="enquiry-message" className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500">Message *</Label>
                <Textarea id="enquiry-message" required rows={5} value={form.message} onChange={set("message")} className="rounded-sm resize-none" placeholder="e.g. 20 × 10 mL vials for our research facility…" data-testid="enquiry-message-input" />
              </div>

              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={sending}
                className="bg-slate-950 text-white py-4 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="enquiry-submit-button"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? "Sending…" : "Send Enquiry"}
              </motion.button>

              <p className="text-red-700 text-xs font-medium leading-snug" data-testid="order-form-disclaimer">
                For research purposes only. Not for human or veterinary use or consumption.
              </p>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
